import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useSiteData } from "@/hooks/useSite";
import { WEEK_ORDER, DAY_LABELS } from "@/lib/site";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Motion";

export default function Footer() {
  const { settings } = useSiteData();
  const c = settings.contact;
  const year = new Date().getFullYear();
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.get("/pages").then(({ data }) => setPages(data.pages || [])).catch(() => {});
  }, []);

  return (
    <footer className="bg-charcoal text-ivory" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-12 pt-20 lg:pt-28 pb-10">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-14 lg:pb-20 border-b border-ivory/10">
            <h2 className="font-display text-4xl lg:text-6xl leading-[1.05] max-w-2xl">
              Il tuo benessere, <em className="text-sand">la tua essenza.</em>
            </h2>
            <Link
              to="/prenota"
              data-testid="footer-cta-button"
              className="inline-flex items-center gap-2 self-start lg:self-auto rounded-full bg-peach text-charcoal text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ivory"
            >
              Prenota una consulenza <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-8 py-14 lg:py-20">
          <div className="md:col-span-4">
            <img src={settings?.brand?.logo || "/images/logo-transparent.png"} alt="K Nutrition" className="h-24 w-auto mb-6" />
            <p className="text-ivory/70 text-[15px] leading-relaxed max-w-xs">
              Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista. Percorsi nutrizionali personalizzati in
              studio a Salerno e online.
            </p>
            {pages.length > 0 && (
              <ul className="mt-6 space-y-2" data-testid="footer-extra-pages">
                {pages.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/pagine/${p.slug}`} data-testid={`footer-page-${p.slug}`} className="text-ivory/70 text-[15px] hover:text-sand transition-colors">
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="md:col-span-4" data-testid="footer-contacts">
            <p className="text-[11px] uppercase tracking-widest2 text-sand/80 mb-5">Contatti</p>
            <ul className="space-y-3 text-[15px]">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 shrink-0 text-sand/70" />
                <span>
                  {c.addressStreet}
                  <br />
                  {c.addressCity}, {c.addressCountry}
                </span>
              </li>
              <li>
                <a href={`tel:${c.phoneHref}`} className="flex items-center gap-3 hover:text-sand transition-colors" data-testid="footer-phone">
                  <Phone size={16} className="shrink-0 text-sand/70" /> {c.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${c.email}`} className="flex items-center gap-3 hover:text-sand transition-colors" data-testid="footer-email">
                  <Mail size={16} className="shrink-0 text-sand/70" /> {c.email}
                </a>
              </li>
              <li>
                <a href={c.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-sand transition-colors" data-testid="footer-instagram">
                  <Instagram size={16} className="shrink-0 text-sand/70" /> {c.instagramHandle}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3" data-testid="footer-hours">
            <p className="text-[11px] uppercase tracking-widest2 text-sand/80 mb-5">Studio · Appuntamenti</p>
            <ul className="space-y-2 text-[15px] text-ivory/80">
              {WEEK_ORDER.map((day) => {
                const windows = (settings.hours || {})[day] || [];
                const label = windows.length
                  ? windows.map((w) => `${w.start}–${w.end}`).join(" · ")
                  : "Non disponibile";
                return (
                  <li key={day} className="flex justify-between gap-6">
                    <span>{DAY_LABELS[day]}</span>
                    <span className={windows.length ? "text-ivory" : "text-ivory/50"}>{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="md:col-span-1">
            <p className="text-[11px] uppercase tracking-widest2 text-sand/80 mb-5">Menu</p>
            <ul className="space-y-2 text-[15px]">
              {["/chi-sono", "/servizi", "/il-metodo", "/prenota"].map((to, i) => (
                <li key={to}>
                  <Link to={to} className="text-ivory/80 hover:text-sand transition-colors">
                    {["Chi sono", "Servizi", "Il metodo", "Prenota"][i]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-8 border-t border-ivory/10 text-[13px] text-ivory/60">
          <p data-testid="footer-copyright">
            © {year} K Nutrition — Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista. Tutti i diritti
            riservati. <span className="text-ivory/40">P.IVA: [da completare]</span>
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-sand transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/cookie-policy" className="hover:text-sand transition-colors" data-testid="footer-cookie-link">Cookie</Link>
            <Link to="/admin" className="text-ivory/35 hover:text-sand transition-colors" data-testid="footer-admin-link">Area riservata</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
